<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useScopeSites } from "@/api/site-query";
import { Site, UserScope } from "@soft-db/monitoring-api-client";
import {
	ColumnSelection,
	DataTableContent,
	DataTablePagination,
	DataTableRoot,
	DataTableSearch,
	Icon,
	makeColumnHelper,
	RowSelectionState,
} from "@soft-db/ui";
import AppDialog from "../common/AppDialog.vue";
import ColumnName from "../common/ColumnName.vue";
import { icPlaceOutline } from "../icons";
import StationLinkedBadge from "../stations/StationLinkedBadge.vue";
import NavBarStatus from "../workspace/NavBarStatus.vue";

const props = defineProps<{
	scope?: UserScope | null;

	/**
	 * A list of sites to hide from the table
	 */
	ignores?: string[];
}>();

const emit = defineEmits<{
	select: [sites: Site[]];
}>();

const { t } = useI18n();

const dialog = defineModel<boolean>("dialog");

const { currentUser } = useCurrentUser();

const { sites: scopeSites } = useScopeSites(
	() => props.scope || currentUser.value?.scope || { type: "none", id: "" }
);

const selection = ref<RowSelectionState>({});

const sites = computed(() => {
	const toIgnore = new Set(props.ignores || []);

	return scopeSites.value?.filter((site) => !toIgnore.has(site.id)) || [];
});

function toggleSelection(site: Site) {
	if (site.id in selection.value) {
		delete selection.value[site.id];
	} else {
		selection.value[site.id] = true;
	}
}

const hasSelection = computed(() => {
	return Object.values(selection.value).some(Boolean);
});

function save() {
	const selectedSites = sites.value.filter((s) => selection.value[s.id] === true);
	emit("select", selectedSites);

	close();
}

function close() {
	selection.value = {};
	dialog.value = false;
}

const { Column } = makeColumnHelper<Site>();
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-[800px]"
		:actionText="t('common.save')"
		:actionDisabled="!hasSelection"
		@click:action="save"
		@close="close"
	>
		<template #title>
			<Icon :d="icPlaceOutline" />
			<span>{{ t("space.addSite") }}</span>
		</template>
		<template #content>
			<DataTableRoot v-model:selection="selection" :data="sites || []">
				<div class="flex flex-col gap-2">
					<DataTableSearch />
					<!-- @vue-generic {Site} -->
					<DataTableContent @click:row="(row) => toggleSelection(row.row.original)">
						<ColumnSelection selectionMode="multiple" />
						<Column
							v-slot="{ row: { original: site } }"
							field="name"
							:header="t('common.name')"
							enableSorting
							enableGlobalFilter
						>
							<ColumnName :name="site.name" :description="site.description" />
						</Column>

						<Column
							id="station"
							v-slot="{ row: { original: site } }"
							:fieldFn="(d) => d.station?.name || ''"
							:header="t('object.station', 1)"
							enableSorting
							enableGlobalFilter
						>
							<StationLinkedBadge :station="site.station" />
						</Column>

						<Column v-slot="{ row: { original: site } }" shrink>
							<NavBarStatus class="mr-4" sourceType="site" :sourceId="site.id" />
						</Column>

						<Column
							id="stationSn"
							hidden
							enableGlobalFilter
							:fieldFn="(site) => site.station?.serialNumber || ''"
						/>
					</DataTableContent>
					<div class="flex md:justify-end">
						<DataTablePagination :defaultPageSize="7" />
					</div>
				</div>
			</DataTableRoot>
		</template>
	</AppDialog>
</template>
